import React, { useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, LinearProgress, Typography, useTheme } from '@mui/material';
import { authenticateBusinessExternal, handleGoogleCallback } from '../../lib/authenticate';
import { useToast } from '../../components/modals/ToastProvider/ToastProvider';
import { getUser, putUser, putUserNotificationSettings } from '../../lib/connector/userQueries';
import { getAuth } from '../../contexts/authStore';
import { useThemeContext } from '../../contexts/ThemeManager/ThemeManager';
import logo from '../../static/logo_main_3124x512.png';
import logoDark from '../../static/logo_main_dark_3124x512.png';
import { createCustomer } from '../../lib/connector/paypalQueries';

const AuthCallback: React.FC = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const toast = useToast();
    const theme = useTheme();
    const { isDarkMode } = useThemeContext();
    const hasRun = useRef(false); // prevent double execution

    useEffect(() => {
        const authCode = searchParams.get('code');
        const error = searchParams.get('error');
        const errorDescription = searchParams.get('error_description');

        if (hasRun.current) return;

        if (error) {
            toast.showError(
                errorDescription || error,
                'Authentication Failed'
            );
            navigate('/login');
            return;
        }

        if (!authCode) {
            toast.showError('No authorization code received', 'Authentication Failed');
            navigate('/login');
            return;
        }

        // Exchange the authorization code for tokens
        handleGoogleCallback(authCode)
            .then(() => {
                try {
                    authenticateBusinessExternal(getAuth().userId!, getAuth().accessToken!, getAuth().idToken!, getAuth().refreshToken!).then(() => {
                        getUser(getAuth().userId!).then((userData) => {
                            if (userData.length === 0) {
                                putUser(getAuth().userId!, "owner", getAuth().name!, getAuth().email!, undefined, new Date().toISOString(), undefined, undefined, new Date().toISOString())
                                putUserNotificationSettings(getAuth().userId!, false, true, false, false, false, false, true)
                                createCustomer(getAuth().userId!, getAuth().name!)
                            }
                            navigate('/dashboard');
                        })
                    })

                } catch (err) {
                    console.error('Error fetching user data:', err);
                }
            })
            .catch((err) => {
                console.error('Token exchange error:', err);
                toast.showError(
                    err.message || 'Failed to complete authentication',
                    'Authentication Failed'
                );
                navigate('/login');
            });
    }, [searchParams]);

    return (
        <Box
            sx={{
                minHeight: '100vh',
                background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.background.paper} 50%, ${theme.palette.background.paper} 100%)`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: 'column',
                gap: 3,
                p: 2
            }}
        >
            <img
                src={isDarkMode ? logoDark : logo}
                alt="Stafflows Logo"
                style={{
                    width: "60%",
                    maxWidth: "400px"
                }}
            />
            <Box sx={{ width: '100%', maxWidth: '300px' }}>
                <LinearProgress 
                    sx={{ 
                        height: 6,
                        borderRadius: 3,
                        backgroundColor: 'rgba(33, 150, 243, 0.1)',
                        '& .MuiLinearProgress-bar': {
                            borderRadius: 3,
                            backgroundColor: '#2196F3'
                        }
                    }} 
                />
            </Box>
            <Typography variant="body1" color="text.primary" sx={{ mt: 1 }}>
                Completing authentication...
            </Typography>
        </Box>
    );
};

export default AuthCallback;
